import { AppConfig, useConfigContext } from './AppConfig';
import { setFirebaseAuthInstance } from './firebaseInstance';
import LoadableContext from './LoadableContext';
import { getApps, initializeApp } from 'firebase/app';
import { Auth, connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, initializeFirestore } from 'firebase/firestore';
import { FirebaseApp } from '@firebase/app';
import { Context, createContext, PropsWithChildren, useContext } from 'react';
import { initFirestore } from '../model/firestoreModel';

export interface Firebase {
    app: FirebaseApp
    auth: Auth
}

export const firebase = async (appConfig: AppConfig): Promise<Firebase> => {
    console.log("Initializing firebase")
    console.log("App config is " + JSON.stringify(appConfig))
    const app = getApps().length === 0 ? initializeApp(appConfig.firebaseConfig, "kantoordag-app") : getApps()[0];
    console.log("App is " + JSON.stringify(app))
    const auth = getAuth(app);
    console.log("Auth is " + JSON.stringify(auth))
    // experimentalForceLongPolling: the SDK's default transport probe (experimentalAutoDetectLongPolling, on by
    // default since v9.22.0) still spends several seconds trying a streaming connection before falling back to
    // long-polling when the browser reaches the Firestore emulator through a Docker/Testcontainers network (e.g.
    // the Playwright browser container) instead of directly. Forcing long-polling skips that probe entirely — this
    // is the Firestore counterpart of https://github.com/firebase/firebase-js-sdk/issues/7888 referenced above for
    // Auth. Only needed against the emulator; against production Firestore the default transport detection is fine.
    const db = initializeFirestore(app, appConfig.emulator?.enabled ? {
        experimentalAutoDetectLongPolling: false,
        experimentalForceLongPolling: true,
    } : {});
    console.log("Firestore is " + JSON.stringify(db))

    initFirestore(db);

    const emulator = appConfig.emulator
    if (emulator?.enabled) {
        const authEmulatorPort = emulator.authPort;
        const firestoreEmulatorPort = emulator.firestorePort;
        console.log(`Using the Auth Emulator on port ${authEmulatorPort}`);
        console.log(`Using the Firestore Emulator on port ${firestoreEmulatorPort}`);

        // Use the page's own hostname rather than a hardcoded one: this works when the browser runs on the
        // host directly (local dev/test) or inside a Testcontainers-managed container on the Dev Services shared
        // network (e.g. the Playwright browser container), where "localhost"/"127.0.0.1" would resolve to the
        // browser's own container instead of the Firebase emulator. See README.md#firebase-emulator-ports about the details
        // how and where the port is configured.
        connectAuthEmulator(auth, `http://${window.location.hostname}:${authEmulatorPort}`);
        connectFirestoreEmulator(db, window.location.hostname, firestoreEmulatorPort);
    } else {
        console.log("Emulator is not enabled")
    }

    // Non-React accessor for Playwright's page.evaluate() (specs/001-msal-login tasks.md T064,
    // firebaseInstance.ts) -- mirrors msalInstance.ts's setMsalInstance() call in Msal.tsx.
    setFirebaseAuthInstance(auth);

    return {
        app,
        auth
    }
}

const _FirebaseContext: Context<Firebase | undefined> = createContext<Firebase | undefined>(undefined);

export const useFirebaseContext = () => {
    const context = useContext(_FirebaseContext);
    if (context === undefined) {
        throw new Error("useFirebaseContext must be used within FirebaseContext");
    }
    return context;
}

export default function FirebaseContext(props: PropsWithChildren) {
    const appConfig = useConfigContext()

    return LoadableContext<Firebase>({
        ...props,
        name: "Firebase",
        context: _FirebaseContext,
        loader: () => firebase(appConfig)
    })
}